/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { NgClass, NgStyle } from '@angular/common';
import {
    AfterViewChecked,
    booleanAttribute,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    viewChild
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatCard, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DashGisNg2ResizableDirective } from '@core/ui/dash-gis-ng2-resizable/dash-gis-ng2-resizable.directive';
import { ResizeEvent } from '@core/ui/dash-gis-ng2-resizable/interfaces';
import { ClassDirective, ShowHideDirective, StyleDirective } from '@ngbracket/ngx-layout';
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { RouterNavigation } from '@ngxs/router-plugin';
import { Actions, ofActionSuccessful } from '@ngxs/store';
import { DeviceService } from '@shared/services/device.service';
import { LoggerService } from '@shared/services/logger.service';
import { Subject } from 'rxjs';

@Component({
    selector: 'dash-gis-flexible-panel',
    styleUrls: [ './flexible-panel.component.scss' ],
    host: {
        '[style.display]': '"block"',
        '[style.width]': 'width()'
    },
    templateUrl: './flexible-panel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LayoutDirective,
        CdkDrag,
        DashGisNg2ResizableDirective,
        NgStyle,
        StyleDirective,
        NgClass,
        ClassDirective,
        MatCard,
        ShowHideDirective,
        FlexDirective,
        CdkDragHandle,
        MatCardHeader,
        LayoutAlignDirective,
        MatCardTitle,
        MatTooltip,
        MatIconButton,
        MatIcon
    ]
})
export class FlexiblePanelComponent implements AfterViewChecked, OnInit, OnDestroy {
    minimized = false;
    panelTitle = input.required<string>();
    enableResize = input(false, { transform: booleanAttribute });
    enableScroll = input(false, { transform: booleanAttribute });
    disableHistory = input(false, { transform: booleanAttribute });
    width = input('800px');

    @Output() closePanel = new EventEmitter<any>();
    @Output() panelBack = new EventEmitter<any>();
    @Output() resizeEnd = new EventEmitter<ResizeEvent>();

    // for resizable container
    style: Partial<CSSStyleDeclaration> = {};

    private destroySubject = new Subject<void>();

    private _resizeHandleWidth = 6;

    constructor(
        private logger: LoggerService,
        private cdr: ChangeDetectorRef,
        private renderer: Renderer2,
        private deviceService: DeviceService,
        private actions$: Actions
    ) {
        this.actions$.pipe(ofActionSuccessful(RouterNavigation)).subscribe(() => this.closePanel.emit());
    }

    ngOnInit() {
    }

    ngAfterViewChecked(): void {
    }

    ngOnDestroy(): void {
        this.destroySubject.next();
    }

    onResizeEnd(event: ResizeEvent) {
        this.resizeEnd.emit(event);
        Object.keys(event.edges).forEach((edge) => {
            switch (edge) {
                case 'top':
                case 'bottom':
                    this.style.top = `${ event.rectangle.top }px`;
                    break;
                case 'right':
                case 'left':
                    this.style.left = `${ event.rectangle.left }px`;
                    break;
            }
        });
        this.style = {
            ...this.style,
            position: 'absolute',
            height: `${ event.rectangle.height }px`,
            width: `${ event.rectangle.width }px`
        };
    }

    onMinimize() {
        this.minimized = true;
    }

    onRestore() {
        this.minimized = false;
    }

    get historyEnabled() {
        return !this.disableHistory() && this.panelBack.observed;
    }

    get closeEnabled() {
        return this.closePanel.observed;
    }

    get isIOS() {
        return this.deviceService.isIOS;
    }

    get resizeHandleWidth() {
        return this._resizeHandleWidth;
    }

    get dragHandleStyle() {
        return {
            position: 'absolute',
            top: `${ this.resizeHandleWidth }px`,
            left: `${ this.resizeHandleWidth }px`,
            right: `${ this.resizeHandleWidth }px`,
            bottom: 0
        };
    }

    private get destroy$() {
        return this.destroySubject.asObservable();
    }
}
